import { IsoDate, IsoDateTime, IsoDateTimeTz, IsoTime, IsoTimeTz, IsoDuration } from '../../../types/iso-date';
import { deconstructDate } from '../../helper/deconstruct-date.function';

/**
 * Converts an ISO date/time/datetime/duration string to a number representation.
 * - For dates/datetimes: returns the UTC timestamp (ms since epoch)
 * - For times: returns milliseconds since midnight
 * - For durations: returns total milliseconds (approximate for years/months)
 */
export function toNumber(
  value: IsoDate | IsoDateTime | IsoDateTimeTz | IsoTime | IsoTimeTz | IsoDuration
): number {
  const d = deconstructDate(value as any);
  if (d._type === 'date') {
    return Date.UTC(d.year, d.month - 1, d.day);
  }
  if (d._type === 'dateTime') {
    return Date.UTC(d.year, d.month - 1, d.day, d.hours, d.minutes, d.seconds, d.milliseconds);
  }
  if (d._type === 'dateTimeTz') {
    let offsetMinutes = 0;
    if (d.offset !== 'Z') {
      offsetMinutes = (d.offset.offsetHours * 60 + d.offset.offsetMinutes) * (d.offset.offsetSign === '-' ? -1 : 1);
    }
    return Date.UTC(d.year, d.month - 1, d.day, d.hours, d.minutes, d.seconds, d.milliseconds) - offsetMinutes * 60000;
  }
  if (d._type === 'time') {
    return d.hours * 3600000 + d.minutes * 60000 + d.seconds * 1000 + d.milliseconds;
  }
  if (d._type === 'timeTz') {
    let offsetMinutes = 0;
    if (d.offset !== 'Z') {
      offsetMinutes = (d.offset.offsetHours * 60 + d.offset.offsetMinutes) * (d.offset.offsetSign === '-' ? -1 : 1);
    }
    const ms = d.hours * 3600000 + d.minutes * 60000 + d.seconds * 1000 + d.milliseconds;
    return ms - offsetMinutes * 60000;
  }
  if (d._type === 'duration') {
    // Approximate: 1 year = 365 days, 1 month = 30 days
    return (
      d.years * 365 * 24 * 60 * 60 * 1000 +
      d.months * 30 * 24 * 60 * 60 * 1000 +
      d.days * 24 * 60 * 60 * 1000 +
      d.hours * 60 * 60 * 1000 +
      d.minutes * 60 * 1000 +
      d.seconds * 1000 +
      d.milliseconds
    );
  }
  throw new Error('toNumber: unsupported ISO value');
}

